import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Users, HeartHandshake, DollarSign, Clock,
  CheckCircle2, XCircle, ChevronDown, ChevronUp, Paperclip, Upload, Trash2, Download, ImageIcon, Loader2, MousePointerClick, TrendingUp, Trophy,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DivisionsManager, EventsManager, MediaManager, BroadcastManager, SchoolsManager, EnrollmentManager, StoreManager, OrdersManager, PromoManager, ForumManager, TestimonialsManager, ChaptersManager, ChapterApplicationsManager, LeaderDirectory } from "@/components/AdminContent";

const StatCard = ({ icon: Icon, label, value }) => (
  <div data-testid="stat-card" className="bg-white border border-slate-200 p-6">
    <Icon className="w-6 h-6 text-[#D90429] mb-4" />
    <div className="font-display text-4xl">{value}</div>
    <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mt-1">{label}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800",
  };
  return (
    <span className={`inline-block px-2.5 py-1 text-xs uppercase tracking-wider font-semibold ${map[status] || "bg-slate-100"}`}>
      {status}
    </span>
  );
};

const AuthImage = ({ fileId, alt, className }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let objUrl;
    let active = true;
    api.get(`/admin/files/${fileId}/download`, { responseType: "blob" })
      .then((r) => {
        objUrl = URL.createObjectURL(r.data);
        if (active) setSrc(objUrl);
        else URL.revokeObjectURL(objUrl);
      })
      .catch(() => {});
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [fileId]);
  if (!src) return <div className={`bg-slate-100 flex items-center justify-center ${className}`}><ImageIcon className="w-7 h-7 text-slate-300" /></div>;
  return <img src={src} alt={alt} className={className} />;
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("applications");
  const [apps, setApps] = useState([]);
  const [donations, setDonations] = useState([]);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [media, setMedia] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [fund, setFund] = useState(null);
  const [savingFund, setSavingFund] = useState(false);
  const [fundHistory, setFundHistory] = useState([]);
  const [welcome, setWelcome] = useState(null);
  const [savingWelcome, setSavingWelcome] = useState(false);
  const [stretch, setStretch] = useState(null);
  const [savingStretch, setSavingStretch] = useState(false);

  const load = async () => {
    try {
      const [a, d, s, m, f, h, w, st] = await Promise.all([
        api.get("/admin/applications"),
        api.get("/admin/donations"),
        api.get("/admin/stats"),
        api.get("/admin/media"),
        api.get("/admin/donation-progress"),
        api.get("/admin/donation-progress/history"),
        api.get("/admin/welcome-email"),
        api.get("/admin/stretch-tiers"),
      ]);
      setApps(a.data);
      setDonations(d.data);
      setStats(s.data);
      setMedia(m.data);
      setFund(f.data);
      setFundHistory(h.data);
      setWelcome(w.data);
      setStretch(st.data.tiers);
    } catch {
      toast.error("Failed to load data");
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${id}`, { status });
      toast.success(`Application ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const openFile = async (fileId) => {
    try {
      const r = await api.get(`/admin/files/${fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.error("Could not open file");
    }
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMedia(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/media", fd);
      toast.success("File uploaded");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingMedia(false);
      e.target.value = "";
    }
  };

  const deleteMedia = async (id) => {
    try {
      await api.delete(`/admin/media/${id}`);
      toast.success("File removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const saveWelcome = async () => {
    setSavingWelcome(true);
    try {
      await api.put("/admin/welcome-email", { subject: welcome.subject, body: welcome.body });
      toast.success("Welcome email updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save welcome email");
    } finally {
      setSavingWelcome(false);
    }
  };

  const saveFund = async () => {
    setSavingFund(true);
    try {
      const { data } = await api.put("/admin/donation-progress", {
        raised: parseFloat(fund.raised) || 0,
        donors: parseInt(fund.donors, 10) || 0,
        goal: parseFloat(fund.goal) || 0,
      });
      toast.success("Mission Fund updated — the goal bar now reflects your new total");
      if (data.milestone_email) {
        toast.success(`Milestone ${data.milestone_email.milestone}% reached — celebration email sent to ${data.milestone_email.recipients} subscriber(s)!`);
      }
      if (data.stretch_email) {
        toast.success(`${data.stretch_email.tiers.join(" & ")} stretch goal unlocked — email sent to ${data.stretch_email.recipients} subscriber(s)!`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update Mission Fund");
    } finally {
      setSavingFund(false);
    }
  };

  const saveStretch = async () => {
    setSavingStretch(true);
    try {
      const { data } = await api.put("/admin/stretch-tiers", {
        tiers: stretch.map((t) => ({ key: t.key, label: t.label, amount: parseFloat(t.amount) || 0 })),
      });
      setStretch(data.tiers);
      toast.success("Stretch goals updated — the homepage now shows your new tiers");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update stretch goals");
    } finally {
      setSavingStretch(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo-emblem.png" alt="logo" className="h-7 w-auto" />
            <span className="font-display text-lg">COMMAND CENTER</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500 hidden sm:inline">{user?.email}</span>
            <button
              data-testid="logout-button"
              onClick={doLogout}
              className="flex items-center gap-2 text-sm uppercase tracking-[0.1em] font-semibold text-[#D90429] hover:opacity-70 transition-opacity duration-200"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
            <StatCard icon={Users} label="Total Applications" value={stats.total_applications} />
            <StatCard icon={Clock} label="Pending Review" value={stats.pending_applications} />
            <StatCard icon={HeartHandshake} label="Donations" value={stats.total_donations} />
            <StatCard icon={DollarSign} label="Total Raised" value={`$${stats.total_raised.toLocaleString()}`} />
            <StatCard icon={MousePointerClick} label="Donate Clicks" value={(stats.donate_clicks ?? 0).toLocaleString()} />
          </div>
        )}

        {/* Mission Fund editor (manual goal-bar total) */}
        {fund && (
          <div data-testid="mission-fund-editor" className="bg-white border border-slate-200 p-5 sm:p-6 mb-10">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-5 h-5 text-[#D90429]" />
              <h3 className="font-display text-lg uppercase">Mission Fund — Goal Bar</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Set the total shown on the homepage donation goal bar. Update these from your GoDaddy payments dashboard whenever new donations come in.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block text-xs uppercase tracking-[0.12em] font-semibold text-slate-500 mb-1.5">Amount Raised ($)</label>
                <input
                  data-testid="fund-raised-input"
                  type="number" min="0" step="0.01"
                  value={fund.raised}
                  onChange={(e) => setFund({ ...fund, raised: e.target.value })}
                  className="w-full border border-slate-300 px-3 py-2.5 focus:outline-none focus:border-[#D90429] transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.12em] font-semibold text-slate-500 mb-1.5">Number of Donors</label>
                <input
                  data-testid="fund-donors-input"
                  type="number" min="0" step="1"
                  value={fund.donors}
                  onChange={(e) => setFund({ ...fund, donors: e.target.value })}
                  className="w-full border border-slate-300 px-3 py-2.5 focus:outline-none focus:border-[#D90429] transition-colors duration-200"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.12em] font-semibold text-slate-500 mb-1.5">Goal ($)</label>
                <input
                  data-testid="fund-goal-input"
                  type="number" min="1" step="0.01"
                  value={fund.goal}
                  onChange={(e) => setFund({ ...fund, goal: e.target.value })}
                  className="w-full border border-slate-300 px-3 py-2.5 focus:outline-none focus:border-[#D90429] transition-colors duration-200"
                />
              </div>
              <button
                data-testid="fund-save-button"
                onClick={saveFund}
                disabled={savingFund}
                className="inline-flex items-center justify-center gap-2 bg-[#D90429] text-white px-6 py-2.5 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-95 disabled:opacity-60"
              >
                {savingFund ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </button>
            </div>

            {fundHistory.length > 0 && (
              <div className="mt-6 border-t border-slate-100 pt-4" data-testid="fund-history">
                <p className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-500 mb-3">Update History</p>
                <div className="max-h-52 overflow-y-auto divide-y divide-slate-100">
                  {fundHistory.map((h) => (
                    <div key={h.id} data-testid="fund-history-row" className="flex items-center justify-between py-2 text-sm">
                      <span className="text-slate-500">
                        {new Date(h.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                        {" · "}
                        {new Date(h.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="font-semibold text-[#0A1128]">${(h.raised ?? 0).toLocaleString()}</span>
                        <span className="text-slate-400">{h.pct}%</span>
                        <span className="text-slate-400 hidden sm:inline">{(h.donors ?? 0).toLocaleString()} donors</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stretch goals editor (Bronze / Silver / Gold tiers) */}
        {stretch && (
          <div data-testid="stretch-tiers-editor" className="bg-white border border-slate-200 p-5 sm:p-6 mb-10">
            <div className="flex items-center gap-2 mb-1">
              <Trophy className="w-5 h-5 text-[#F4A261]" />
              <h3 className="font-display text-lg uppercase">Stretch Goals — Bronze / Silver / Gold</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Set the dollar amount for each stretch tier shown on the homepage Mission Fund. Each tier unlocks a badge + confetti and sends a celebration email to subscribers the first time the raised total crosses it.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {stretch.map((t, i) => (
                <div key={t.key || i} className="border border-slate-200 p-4">
                  <label className="block text-xs uppercase tracking-[0.12em] font-semibold text-slate-500 mb-1.5">Tier Label</label>
                  <input
                    data-testid={`stretch-label-${t.key || i}`}
                    value={t.label}
                    onChange={(e) => setStretch(stretch.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))}
                    className="w-full border border-slate-300 px-3 py-2.5 mb-3 focus:outline-none focus:border-[#D90429] transition-colors duration-200"
                  />
                  <label className="block text-xs uppercase tracking-[0.12em] font-semibold text-slate-500 mb-1.5">Amount ($)</label>
                  <input
                    data-testid={`stretch-amount-${t.key || i}`}
                    type="number" min="1" step="0.01"
                    value={t.amount}
                    onChange={(e) => setStretch(stretch.map((x, xi) => xi === i ? { ...x, amount: e.target.value } : x))}
                    className="w-full border border-slate-300 px-3 py-2.5 focus:outline-none focus:border-[#D90429] transition-colors duration-200"
                  />
                </div>
              ))}
            </div>
            <button
              data-testid="stretch-save-button"
              onClick={saveStretch}
              disabled={savingStretch}
              className="mt-4 inline-flex items-center justify-center gap-2 bg-[#D90429] text-white px-6 py-2.5 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-95 disabled:opacity-60"
            >
              {savingStretch ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Stretch Goals"}
            </button>
          </div>
        )}

        {/* Approval welcome email editor */}
        {welcome && (
          <div data-testid="welcome-email-editor" className="bg-white border border-slate-200 p-5 sm:p-6 mb-10">
            <div className="flex items-center gap-2 mb-1">
              <HeartHandshake className="w-5 h-5 text-[#D90429]" />
              <h3 className="font-display text-lg uppercase">Approval Welcome Email</h3>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Emailed to applicants the moment you approve them. Use <code className="bg-slate-100 px-1">{"{name}"}</code> to insert their name. The Forum/Events links and the "Set Your Password" button are added automatically.
            </p>
            <label className="block text-xs uppercase tracking-[0.12em] font-semibold text-slate-500 mb-1.5">Subject</label>
            <input
              data-testid="welcome-subject-input"
              value={welcome.subject}
              onChange={(e) => setWelcome({ ...welcome, subject: e.target.value })}
              className="w-full border border-slate-300 px-3 py-2.5 mb-4 focus:outline-none focus:border-[#D90429] transition-colors duration-200"
            />
            <label className="block text-xs uppercase tracking-[0.12em] font-semibold text-slate-500 mb-1.5">Message</label>
            <textarea
              data-testid="welcome-body-input"
              value={welcome.body}
              onChange={(e) => setWelcome({ ...welcome, body: e.target.value })}
              rows={4}
              className="w-full border border-slate-300 px-3 py-2.5 resize-none focus:outline-none focus:border-[#D90429] transition-colors duration-200"
            />
            <button
              data-testid="welcome-save-button"
              onClick={saveWelcome}
              disabled={savingWelcome}
              className="mt-3 inline-flex items-center justify-center gap-2 bg-[#D90429] text-white px-6 py-2.5 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-95 disabled:opacity-60"
            >
              {savingWelcome ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </button>
          </div>
        )}


        <div className="flex gap-1 border-b border-slate-200 mb-6">
          {[["applications", "Applications"], ["donations", "Donations"], ["store", "Store"], ["orders", "Orders"], ["promos", "Promo Codes"], ["divisions", "Divisions"], ["events", "Events"], ["schools", "Schools"], ["enrollment", "School Leads"], ["testimonials", "Testimonials"], ["chapters", "Chapters"], ["chapter-apps", "Chapter Applications"], ["leaders", "Leader Directory"], ["mediaposts", "Media Page"], ["broadcast", "Broadcast"], ["forum", "Forum"], ["media", "Media Library"]].map(([key, label]) => (
            <button
              key={key}
              data-testid={`tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-6 py-3 uppercase tracking-[0.15em] text-sm font-semibold transition-colors duration-200 border-b-2 -mb-px ${
                tab === key ? "border-[#D90429] text-[#0A1128]" : "border-transparent text-slate-400 hover:text-slate-600"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Applications */}
        {tab === "applications" && (
          <div data-testid="applications-panel" className="space-y-3">
            {apps.length === 0 && <p className="text-slate-400 py-12 text-center">No applications yet.</p>}
            {apps.map((a) => (
              <div key={a.id} data-testid="application-row" className="bg-white border border-slate-200">
                <div
                  className="flex items-center justify-between p-5 cursor-pointer"
                  onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-semibold text-lg">{a.full_name}</span>
                      <StatusBadge status={a.status} />
                    </div>
                    <div className="text-sm text-slate-500 mt-1 truncate">
                      {a.email} · {a.state_city} · {a.occupation}
                    </div>
                  </div>
                  {expanded === a.id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                </div>

                {expanded === a.id && (
                  <div className="px-5 pb-5 border-t border-slate-100 pt-4 grid sm:grid-cols-2 gap-4 text-sm">
                    <div><span className="text-slate-400 uppercase text-xs tracking-wider block">Phone</span>{a.phone}</div>
                    <div><span className="text-slate-400 uppercase text-xs tracking-wider block">Age</span>{a.age}</div>
                    <div className="sm:col-span-2"><span className="text-slate-400 uppercase text-xs tracking-wider block">Skills</span>{a.skills}</div>
                    <div className="sm:col-span-2"><span className="text-slate-400 uppercase text-xs tracking-wider block">Reason</span>{a.reason}</div>
                    <div className="sm:col-span-2"><span className="text-slate-400 uppercase text-xs tracking-wider block">Prior Experience</span>{a.prior_experience || "—"}</div>
                    {a.attachment && (
                      <div className="sm:col-span-2">
                        <span className="text-slate-400 uppercase text-xs tracking-wider block mb-1">Attachment</span>
                        <button
                          data-testid={`attachment-${a.id}`}
                          onClick={() => openFile(a.attachment.id)}
                          className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-[#0A1128] px-4 py-2 text-sm font-semibold transition-colors duration-200"
                        >
                          <Paperclip className="w-4 h-4 text-[#D90429]" /> {a.attachment.original_filename}
                        </button>
                      </div>
                    )}
                    <div className="sm:col-span-2 flex gap-3 pt-2">
                      <button
                        data-testid={`approve-${a.id}`}
                        onClick={() => updateStatus(a.id, "approved")}
                        className="flex items-center gap-2 bg-green-600 text-white px-5 py-2.5 uppercase tracking-wider text-xs font-bold hover:bg-green-700 transition-colors duration-200"
                      >
                        <CheckCircle2 className="w-4 h-4" /> Approve
                      </button>
                      <button
                        data-testid={`reject-${a.id}`}
                        onClick={() => updateStatus(a.id, "rejected")}
                        className="flex items-center gap-2 bg-[#0A1128] text-white px-5 py-2.5 uppercase tracking-wider text-xs font-bold hover:bg-[#1a2547] transition-colors duration-200"
                      >
                        <XCircle className="w-4 h-4" /> Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Donations */}
        {tab === "donations" && (
          <div data-testid="donations-panel" className="bg-white border border-slate-200 overflow-x-auto">
            {donations.length === 0 ? (
              <p className="text-slate-400 py-12 text-center">No donations yet.</p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="bg-muted">
                  <tr className="uppercase text-xs tracking-wider text-slate-500">
                    <th className="px-5 py-4">Donor</th>
                    <th className="px-5 py-4">Email</th>
                    <th className="px-5 py-4">Amount</th>
                    <th className="px-5 py-4">Type</th>
                    <th className="px-5 py-4">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {donations.map((d) => (
                    <tr key={d.session_id} data-testid="donation-row" className="border-t border-slate-100">
                      <td className="px-5 py-4 font-medium">{d.donor_name || "Anonymous"}</td>
                      <td className="px-5 py-4 text-slate-500">{d.donor_email || "—"}</td>
                      <td className="px-5 py-4 font-display text-lg text-[#D90429]">${d.amount}</td>
                      <td className="px-5 py-4">
                        <span className={`inline-block px-2.5 py-1 text-xs uppercase tracking-wider font-semibold ${d.frequency === "monthly" ? "bg-[#0A1128] text-white" : "bg-slate-100 text-slate-600"}`}>
                          {d.frequency === "monthly" ? "Monthly" : "One-Time"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-slate-500">{new Date(d.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Divisions */}
        {tab === "divisions" && <DivisionsManager />}

        {/* Events */}
        {tab === "events" && <EventsManager />}

        {tab === "schools" && <SchoolsManager />}

        {tab === "store" && <StoreManager />}

        {tab === "orders" && <OrdersManager />}

        {tab === "promos" && <PromoManager />}

        {tab === "enrollment" && <EnrollmentManager />}

        {tab === "testimonials" && <TestimonialsManager />}

        {tab === "chapters" && <ChaptersManager />}

        {tab === "chapter-apps" && <ChapterApplicationsManager />}

        {tab === "leaders" && <LeaderDirectory />}

        {tab === "mediaposts" && <MediaManager />}

        {tab === "broadcast" && <BroadcastManager />}
        {tab === "forum" && <ForumManager />}

        {/* Media Library */}
        {tab === "media" && (
          <div data-testid="media-panel">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <p className="text-slate-500 text-sm">Upload and manage images and documents for your site. Images (JPG, PNG, GIF, WEBP) and PDFs up to 15MB.</p>
              <label data-testid="media-upload-label" className={`inline-flex items-center gap-2 bg-[#D90429] text-white px-6 py-3 uppercase tracking-[0.15em] text-xs font-bold cursor-pointer hover:bg-[#b0031f] transition-colors duration-200 ${uploadingMedia ? "opacity-60 pointer-events-none" : ""}`}>
                {uploadingMedia ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload File</>}
                <input type="file" data-testid="media-upload-input" onChange={handleMediaUpload} accept="image/*,application/pdf" className="hidden" />
              </label>
            </div>
            {media.length === 0 ? (
              <p className="text-slate-400 py-12 text-center bg-white border border-slate-200">No media uploaded yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {media.map((f) => (
                  <div key={f.id} data-testid="media-item" className="bg-white border border-slate-200 overflow-hidden group">
                    <div className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden">
                      {f.content_type?.startsWith("image/") ? (
                        <AuthImage fileId={f.id} alt={f.original_filename} className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex flex-col items-center text-slate-400"><Paperclip className="w-8 h-8" /><span className="text-xs mt-2 uppercase">{f.original_filename.split(".").pop()}</span></div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-xs text-slate-600 truncate" title={f.original_filename}>{f.original_filename}</p>
                      <div className="flex gap-2 mt-2">
                        <button data-testid={`media-download-${f.id}`} onClick={() => openFile(f.id)} className="flex-1 flex items-center justify-center gap-1 bg-slate-100 hover:bg-slate-200 text-[#0A1128] py-2 text-xs font-semibold transition-colors duration-200"><Download className="w-3.5 h-3.5" /> Open</button>
                        <button data-testid={`media-delete-${f.id}`} onClick={() => deleteMedia(f.id)} className="flex items-center justify-center bg-slate-100 hover:bg-[#D90429] hover:text-white text-[#D90429] px-3 py-2 transition-colors duration-200"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
